/**
 * Platform Admin Redirect
 *
 * Effect-based redirect for platform admins without team membership.
 * When a platform admin without a team tries to access team-scoped routes,
 * this hook redirects them to /admin/teams.
 *
 * IMPORTANT:
 * - Must be hook-safe: always callable from parents without conditional hook order issues
 * - Redirect is effect-based (not during render) to prevent flicker/loops
 */

import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTeam } from '@/contexts/TeamContext';
import { useGlobalAdminContext } from '@/contexts/GlobalAdminContext';
import { logger } from '@/lib/logger';

type PlatformAdminRedirectOptions = {
  /**
   * Enables/disables the redirect logic.
   * The hook still runs (and still calls hooks internally), but it no-ops when disabled.
   * This prevents "Rendered more hooks than during the previous render" errors upstream.
   *
   * Default: true
   */
  enabled?: boolean;

  /**
   * Where to redirect platform admins without a team.
   * Default: /admin/teams
   */
  to?: string;
};

export function usePlatformAdminRedirect(options: PlatformAdminRedirectOptions = {}): void {
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading: authLoading } = useAuth();
  const { loading: teamLoading, activeTeamId, error: teamError } = useTeam();
  const { isPlatformAdmin, isLoading: adminLoading } = useGlobalAdminContext();

  const enabled = options.enabled ?? true;
  const to = options.to ?? '/admin/teams';

  // Prevent repeated redirects per route
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    // Reset redirect flag when route changes
    hasRedirectedRef.current = false;
  }, [location.pathname]);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');

  // Compute eligibility deterministically (no side effects)
  const shouldRedirect = useMemo(() => {
    if (!enabled) return false;
    if (authLoading || teamLoading || adminLoading) return false;
    if (hasRedirectedRef.current) return false;
    if (isAdminRoute || isOnboardingRoute) return false;

    // Only redirect platform admins without a team (definitive NO_TEAM)
    return isPlatformAdmin && !activeTeamId && teamError === 'NO_TEAM';
  }, [
    enabled,
    authLoading,
    teamLoading,
    adminLoading,
    isAdminRoute,
    isOnboardingRoute,
    isPlatformAdmin,
    activeTeamId,
    teamError,
  ]);

  useEffect(() => {
    if (!shouldRedirect) return;

    logger.info('[PlatformAdminRedirect] Platform admin without team, redirecting', {
      from: location.pathname,
      to,
    });

    hasRedirectedRef.current = true;
    navigate(to, { replace: true });
  }, [shouldRedirect, location.pathname, to, navigate]);
}
